import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Col,
  Divider,
  Flex,
  Form,
  FormInstance,
  Menu,
  message,
  Row,
  Typography,
} from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import { EllipsisOutlined } from '@ant-design/icons';
import CustomInput from '../../components/CustomInput';
import CustomTitle from '../../components/CustomTitle.jsx';
import CustomButton from '../../components/CustomButton.jsx';
import TeamsSelector from '../../components/TeamsSelector.jsx';
import StatusTag from '../../components/StatusTag.jsx';
import AddItemButton from '../../components/AddItemButton.jsx';
import infoIcon from '../../assets/images/info-icon.svg';
import CustomDeleteModal from '../Modals/CustomDeleteModal.jsx';
import NotificationBanner from '../../components/NotificationBanner.jsx';
import { getTeamsDDLOFFeature } from '../../common/utils/dropDown';
import useToken from '../../common/helper/useToken';
import { LinkSettingsCourierAccount } from '../../apis/settings.js';
import { useSpin } from '../../components/SpinContext.jsx';
import { useNavigate } from 'react-router-dom';
import { getProviderImagePath } from '../../common/utils/imageUtils';
import { useSelector } from 'react-redux';
import { DeleteIcon, PencilIcon } from '@/assets/icons/icons';
import './TransportProviderDetails.css';
import DirectContactManage from './DirectContactList/DirectContactManage';
import type { Contact, CourierProviderData } from './DirectContactList/types';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';

interface Team {
  id: string;
  name: string;
}

interface ExtendedProviderData extends Partial<CourierProviderData> {
  courier_name?: string;
  description?: string;
  teams?: Team[];
}

interface ProviderDetailsHeaderProps {
  provider: ExtendedProviderData;
  edit: boolean;
  handleMenuClick: (key: string) => void;
}

export interface CourierAccountEntry {
  /** Temporary client-side key for list rendering before save. */
  _key: string;
  account_number: string;
  description: string;
  is_default: boolean;
  sort_order: number;
  /** Persisted UUID — undefined for rows not yet saved to the backend. */
  id?: string;
}

interface TransportProviderDetailsProps {
  workingContacts: Contact[];
  form?: FormInstance;
  setWorkingContacts: (contacts: Contact[]) => void;
  setDrawerView: (view: string) => void;
  setEditingContact: (contact: unknown) => void;
  setIsAddingSupport?: (value: boolean) => void;
  providerData: ExtendedProviderData;
  onClose: () => void;
  edit: boolean;
  setContactType: (type: string) => void;
  handleEditFromList: (contact: unknown) => void;
  handleOpenAddNote: (contact: unknown) => void;
  setModalState: (
    fn: (prev: Record<string, boolean>) => Record<string, boolean>
  ) => void;
  recordId?: number | null;
  courierId?: number | null;
  selectedTeams: Team[];
  setSelectedTeams: (teams: Team[]) => void;
  drawerView?: string;
}

interface RootState {
  teams: {
    list: Team[];
  };
}

const ProviderDetailsHeader = ({
  provider,
  edit,
  handleMenuClick,
}: ProviderDetailsHeaderProps) => {
  const menu = (
    <Menu onClick={({ key }) => handleMenuClick(key)}>
      <Menu.Item key='connection'>
        <span className='tp-details-menu-primary'>Change credentials</span>
      </Menu.Item>
      <Menu.Item key='delete' style={{ color: 'red' }}>
        Remove
      </Menu.Item>
    </Menu>
  );

  return (
    <div className='tp-details-header'>
      <Flex align='center' justify='space-between'>
        <Flex align='center' gap={12}>
          <Avatar
            src={
              <img
                src={getProviderImagePath(
                  provider?.title || provider?.courier_name,
                  'transportProvider'
                )}
                alt='avatar'
                className='tp-details-avatar-img'
              />
            }
            size={50}
            style={{
              backgroundColor: 'transparent',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#333',
            }}
          ></Avatar>
          <Flex vertical>
            <CustomTitle
              marginBottom={'5px'}
              text={provider?.courier_name || provider?.title}
              fontSize={24}
              fontWeight='700'
              color='var(--text-primary)'
            />
            <StatusTag
              status={
                provider?.value === 'new'
                  ? 'pending'
                  : provider?.value === 'basic'
                    ? 'basic'
                    : (provider?.status ?? '')
              }
            />
          </Flex>
        </Flex>

        {edit && (
          <Dropdown overlay={menu} trigger={['click']}>
            <div className='tp-details-menu-btn'>
              <EllipsisOutlined className='tp-details-menu-icon' />
            </div>
          </Dropdown>
        )}
      </Flex>
    </div>
  );
};

const TransportProviderDetails = ({
  workingContacts,
  form: injectedForm,
  setWorkingContacts,
  setDrawerView,
  setEditingContact,
  setIsAddingSupport,
  providerData,
  onClose,
  edit,
  setContactType,
  handleEditFromList,
  handleOpenAddNote,
  setModalState,
  recordId = null,
  courierId = null,
  selectedTeams,
  setSelectedTeams,
  drawerView,
}: TransportProviderDetailsProps) => {
  const [internalForm] = Form.useForm();
  const form = injectedForm ?? internalForm;
  const navigate = useNavigate();
  const { showSpin, hideSpin } = useSpin();
  const { token, removeToken } = useToken();
  const [_isFormValid, setIsFormValid] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [notification] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<unknown>(null);
  const [objID, setObjectID] = useState<number | null>(null);

  // Account numbers state
  const [accounts, setAccounts] = useState<CourierAccountEntry[]>([]);
  const [newAccountNumber, setNewAccountNumber] = useState('');
  const [newAccountDescription, setNewAccountDescription] = useState('');
  const [editingAccountKey, setEditingAccountKey] = useState<string | null>(
    null
  );
  const [errors, setErrors] = useState({
    accountNumber: '',
    description: '',
  });
  const teamsList = useSelector((state: RootState) => state.teams.list);

  const hasValidTeams = !(
    teamsList?.length === 0 ||
    (teamsList?.length === 1 && teamsList[0].name.toLowerCase() === 'default')
  );

  const [allTeams, setAllTeams] = useState<Team[]>([]);

  useEffect(() => {
    const _ = getTeamsDDLOFFeature(token ?? '', 'Transport providers').then(
      (val) => {
        if (val && val.length > 0) {
          setAllTeams(val);
        }
      }
    );
  }, [token]);

  const unauthorize = useCallback(
    (error: { status?: number }) => {
      if (error && error?.status === 401) {
        removeToken();
        const _ = navigate('/login');
      }
    },
    [navigate, removeToken]
  );

  const prevCourierIdRef = useRef<number | null | undefined>(undefined);

  // Depends on the seeded fields, not providerData, so a credentials-only update keeps drafts.
  const hasProviderData = Boolean(providerData);
  const providerName = providerData?.courier_name || providerData?.title || '';
  const providerDescription = providerData?.description || '';
  const providerTeams = providerData?.teams;
  const rawAccounts = (providerData as Record<string, unknown> | undefined)
    ?.accounts;

  useEffect(() => {
    if (hasProviderData) {
      form.setFieldsValue({
        name: providerName,
        description: providerDescription,
        teams: providerTeams ?? [],
      });

      if (providerTeams) {
        setSelectedTeams(providerTeams);
      }

      // Seed account numbers from provider data — always reset so switching
      // between providers never leaks accounts from one into another.
      setAccounts(
        Array.isArray(rawAccounts) && rawAccounts.length > 0
          ? rawAccounts.map((a: Record<string, unknown>, i: number) => ({
              _key: String(a.id ?? `existing-${i}`),
              id: a.id as string | undefined,
              account_number: String(a.account_number ?? ''),
              description: String(a.description ?? ''),
              is_default: Boolean(a.is_default),
              sort_order: Number(a.sort_order ?? i),
            }))
          : []
      );
    }
  }, [
    hasProviderData,
    providerName,
    providerDescription,
    providerTeams,
    rawAccounts,
    form,
    setSelectedTeams,
  ]);

  useEffect(() => {
    // Reset "Add Account" typing states and errors if:
    // 1. We switch to a different provider (courierId changes or initial open)
    // 2. We navigate away from the main details panel (drawerView changes to a sub-view)
    if (
      prevCourierIdRef.current !== courierId ||
      (drawerView && drawerView !== 'details')
    ) {
      setEditingAccountKey(null);
      setNewAccountNumber('');
      setNewAccountDescription('');
      setErrors({
        accountNumber: '',
        description: '',
      });
    }

    if (providerData) {
      prevCourierIdRef.current = courierId;
    } else {
      prevCourierIdRef.current = null;
    }
  }, [courierId, drawerView, providerData]);

  useEffect(() => {
    if (edit && recordId) setObjectID(recordId);
  }, [edit, recordId]);

  const handleMenuClick = (key: string) => {
    setModalState((prev: Record<string, boolean>) => ({
      ...prev,
      [key]: true,
    }));
  };

  const handleAddNewContact = () => {
    setIsAddingSupport?.(false);
    setEditingContact(null);
    setContactType('direct_contact');
    setDrawerView('addContact');
  };

  const handleConfirmDelete = () => {
    if (!contactToDelete) {
      return;
    }
    setDeleteModalVisible(false);
    setContactToDelete(null);
  };

  useEffect(() => {
    form.setFieldsValue({ teams: selectedTeams ?? [] });
  }, [selectedTeams, form]);

  const onFinish = (values: Record<string, unknown>) => {
    const teamIds = selectedTeams.map((team: Team) => team.id);
    if (values && Object.keys(values).length > 0) {
      const contactsForApi = (workingContacts ?? []).map(
        (c: Contact, i: number) => ({
          ...c.api,
          order_index: i,
        })
      );
      const accountsForApi = accounts.map((a, i) => ({
        ...(a.id ? { id: a.id } : {}),
        account_number: a.account_number,
        description: a.description,
        is_default: a.is_default,
        sort_order: i,
      }));
      const model: Record<string, unknown> = {
        ...form.getFieldsValue(),
        contacts: contactsForApi,
        accounts: accountsForApi,
        teams: teamIds,
      };
      if (courierId) {
        model.courier = courierId;
      }

      if (providerData?.credentials) {
        model.credentials = providerData.credentials;
      }

      LinkCouriers(model);
    }
  };

  const LinkCouriers = (model: Record<string, unknown>) => {
    if (edit === true) {
      model.id = objID;
    }
    setIsFormValid(true);
    showSpin();

    if (Array.isArray(model?.contacts)) {
      model.contacts = model.contacts.map((contact) => {
        if (contact?.default === true) {
          return { ...contact, contact_type: 'general_support' };
        }
        return contact;
      });
    }

    LinkSettingsCourierAccount(token ?? '', model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data.is_success) {
            onClose();
            message.success(data?.data?.message);
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((err) => {
        unauthorize(err);
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  return (
    <>
      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        className='tp-details-form'
      >
        <div className='tp-details-header-wrap'>
          <ProviderDetailsHeader
            provider={providerData}
            edit={edit}
            handleMenuClick={handleMenuClick}
          />
        </div>
        {notification && (
          <NotificationBanner
            icon={infoIcon}
            message='A Zoomlogi representative will reach out to you to complete integration'
          />
        )}
        <div className='custom-scrollbar tp-details-body'>
          <Row gutter={[16, 14]}>
            <Col span={24}>
              <Form.Item
                className='custom-input form-input-style'
                label='Description'
                rules={[
                  { required: false, message: 'Please enter description' },
                ]}
                name='description'
              >
                <CustomInput
                  placeholder={`Enter provider description`}
                  bgColor='var(--light-gray)'
                  height='36px'
                  textColor='var(--text-primary)'
                  borderRadius='8px'
                  border='none'
                  placeholderColor='var(--text-secondary)'
                />
              </Form.Item>
            </Col>

            {hasValidTeams && (
              <Col span={24}>
                <Form.Item
                  className='custom-input form-input-style'
                  label={`${themeT('team', theme.themeType)}(s)`}
                  rules={[{ required: true, message: 'Please select value' }]}
                  name='teams'
                >
                  <TeamsSelector
                    teamsOptions={allTeams}
                    selectedTeams={selectedTeams}
                    onChange={setSelectedTeams}
                    formState={edit === true ? 'edit' : 'add'}
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={24}>
              <Divider
                style={{ borderColor: 'var(--gray-300)', margin: '0px' }}
              />
            </Col>
            <Col span={24}>
              <CustomTitle
                text='Contacts'
                marginBottom={'0px'}
                fontSize={'18px'}
                lineHeight={'24px'}
                fontWeight='700'
              />
            </Col>
            <div className='tp-details-contacts-wrap'>
              <DirectContactManage
                externalItems={workingContacts}
                onChange={setWorkingContacts}
                onEdit={handleEditFromList}
                /* @ts-expect-error TODO fix types */
                providerData={providerData}
                title='When using AI to resolve, these will be contacted in order (top → bottom)'
                onAddNote={handleOpenAddNote}
              />
            </div>
            <Col span={24}>
              <Divider
                style={{ borderColor: 'var(--gray-300)', margin: '0px' }}
              />
            </Col>
            <Col span={24}>
              <AddItemButton
                gap='24px'
                width='28px'
                height='28px'
                marginTop='0px'
                text='Add Contact'
                fontSize='12px'
                fontWeight='600'
                onClick={handleAddNewContact}
              />
            </Col>

            {/* ── Account Numbers ─────────────────────────────────────── */}
            <Col span={24}>
              <Divider
                style={{ borderColor: 'var(--gray-300)', margin: '0px' }}
              />
            </Col>
            <Col span={24}>
              <CustomTitle
                text='Account Numbers'
                marginBottom={'4px'}
                fontSize={'18px'}
                lineHeight={'24px'}
                fontWeight='700'
              />
              <p className='tp-details-hint'>
                Add multiple billing accounts. The default will be pre-selected
                when quoting.
              </p>
            </Col>

            {accounts.map((acct, idx) => (
              <Col span={24} key={acct._key}>
                {editingAccountKey === acct._key ? (
                  <div className='tp-details-account-form tp-details-account-form--spaced'>
                    <CustomInput
                      placeholder='Account number'
                      bgColor='var(--light-gray)'
                      height='36px'
                      textColor='var(--text-primary)'
                      borderRadius='8px'
                      border='none'
                      value={newAccountNumber}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;

                        if (value.length <= 40) {
                          setNewAccountNumber(value);
                          setErrors((prev) => ({ ...prev, accountNumber: '' }));
                        } else {
                          setErrors((prev) => ({
                            ...prev,
                            accountNumber: 'Maximum 40 characters allowed',
                          }));
                        }
                      }}
                    />
                    {errors.accountNumber && (
                      <div className='TP-account-error'>
                        {errors.accountNumber}
                      </div>
                    )}
                    <div className='tp-details-field-gap'>
                      <CustomInput
                        placeholder='Description (optional)'
                        bgColor='var(--light-gray)'
                        height='36px'
                        textColor='var(--text-primary)'
                        borderRadius='8px'
                        border='none'
                        value={newAccountDescription}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value;

                          if (value.length <= 300) {
                            setNewAccountDescription(value);
                            setErrors((prev) => ({ ...prev, description: '' }));
                          } else {
                            setErrors((prev) => ({
                              ...prev,
                              description: 'Maximum 300 characters allowed',
                            }));
                          }
                        }}
                      />
                      {errors.description && (
                        <div className='TP-account-error'>
                          {errors.description}
                        </div>
                      )}
                    </div>
                    <div className='tp-details-form-actions'>
                      <CustomButton
                        text='Cancel'
                        bgColor='transparent'
                        border='1px solid #E8E8E8'
                        borderRadius='6px'
                        color='var(--text-primary)'
                        fontSize='12px'
                        fontWeight='500'
                        height='32px'
                        width='auto'
                        boxShadow='none'
                        onClick={() => {
                          setEditingAccountKey(null);
                          setNewAccountNumber('');
                          setNewAccountDescription('');
                        }}
                      />
                      <CustomButton
                        text='Save'
                        bgColor='#000'
                        borderRadius='6px'
                        color='#fff'
                        fontSize='12px'
                        fontWeight='600'
                        height='32px'
                        width='auto'
                        onClick={() => {
                          if (!newAccountNumber.trim()) return;
                          setAccounts((prev) =>
                            prev.map((a) =>
                              a._key === acct._key
                                ? {
                                    ...a,
                                    account_number: newAccountNumber.trim(),
                                    description: newAccountDescription.trim(),
                                  }
                                : a
                            )
                          );
                          setEditingAccountKey(null);
                          setNewAccountNumber('');
                          setNewAccountDescription('');
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className='tp-details-account-row'>
                    <div
                      className={
                        acct?.is_default ? 'account-default' : 'account-width'
                      }
                    >
                      <div className='tp-details-account-title'>
                        <Typography.Text
                          className={`TP-account-number ${acct?.is_default ? 'is-default' : ''} `}
                          ellipsis={{
                            tooltip: acct?.account_number,
                          }}
                        >
                          {acct?.account_number}
                        </Typography.Text>
                        {acct.is_default && (
                          <span className='tp-details-default-badge'>
                            Default
                          </span>
                        )}
                      </div>
                      {acct.description && (
                        <div className='tp-details-account-desc'>
                          <Typography.Text
                            className={`TP-account-number `}
                            ellipsis={{
                              tooltip: acct?.description,
                            }}
                          >
                            {acct?.description}
                          </Typography.Text>
                        </div>
                      )}
                    </div>
                    <div className='tp-details-row-actions'>
                      <button
                        type='button'
                        className={`tp-details-set-default-btn${acct.is_default ? ' tp-details-set-default-btn--default' : ''}`}
                        onClick={() =>
                          !acct.is_default &&
                          setAccounts((prev) =>
                            prev.map((a, i) => ({
                              ...a,
                              is_default: i === idx,
                            }))
                          )
                        }
                      >
                        Set default
                      </button>
                      <button
                        type='button'
                        className='tp-details-icon-btn'
                        aria-label='Edit'
                        onClick={() => {
                          setEditingAccountKey(acct._key);
                          setNewAccountNumber(acct.account_number);
                          setNewAccountDescription(acct.description);
                        }}
                      >
                        <PencilIcon color='var(--text-primary)' />
                      </button>
                      <button
                        type='button'
                        className='tp-details-icon-btn'
                        aria-label='Remove'
                        onClick={() =>
                          setAccounts((prev) => {
                            const next = prev.filter(
                              (a) => a._key !== acct._key
                            );
                            if (acct.is_default && next.length > 0) {
                              next[0] = { ...next[0], is_default: true };
                            }
                            return next;
                          })
                        }
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </div>
                )}
              </Col>
            ))}

            <Col span={24}>
              {editingAccountKey === '__new__' ? (
                <div className='tp-details-account-form'>
                  <CustomInput
                    placeholder='Account number'
                    bgColor='var(--light-gray)'
                    height='36px'
                    textColor='var(--text-primary)'
                    borderRadius='8px'
                    border='none'
                    value={newAccountNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = e.target.value;

                      if (value.length <= 40) {
                        setNewAccountNumber(value);
                        setErrors((prev) => ({ ...prev, accountNumber: '' }));
                      } else {
                        setErrors((prev) => ({
                          ...prev,
                          accountNumber: 'Maximum 40 characters allowed',
                        }));
                      }
                    }}
                  />
                  {errors.accountNumber && (
                    <div className='TP-account-error'>
                      {errors.accountNumber}
                    </div>
                  )}
                  <div className='tp-details-field-gap'>
                    <CustomInput
                      placeholder='Description (optional)'
                      bgColor='var(--light-gray)'
                      height='36px'
                      textColor='var(--text-primary)'
                      borderRadius='8px'
                      border='none'
                      value={newAccountDescription}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;

                        if (value.length <= 300) {
                          setNewAccountDescription(value);
                          setErrors((prev) => ({ ...prev, description: '' }));
                        } else {
                          setErrors((prev) => ({
                            ...prev,
                            description: 'Maximum 300 characters allowed',
                          }));
                        }
                      }}
                    />
                    {errors.description && (
                      <div className='TP-account-error'>
                        {errors.description}
                      </div>
                    )}
                  </div>
                  <div className='tp-details-form-actions'>
                    <CustomButton
                      text='Cancel'
                      bgColor='transparent'
                      border='1px solid #E8E8E8'
                      borderRadius='6px'
                      color='var(--text-primary)'
                      fontSize='12px'
                      fontWeight='500'
                      height='32px'
                      width='auto'
                      boxShadow='none'
                      onClick={() => {
                        setEditingAccountKey(null);
                        setNewAccountNumber('');
                        setNewAccountDescription('');
                      }}
                    />
                    <CustomButton
                      text='Add'
                      bgColor='#000'
                      borderRadius='6px'
                      color='#fff'
                      fontSize='12px'
                      fontWeight='600'
                      height='32px'
                      width='auto'
                      onClick={() => {
                        if (!newAccountNumber.trim()) return;
                        const isFirst = accounts.length === 0;
                        setAccounts((prev) => [
                          ...prev,
                          {
                            _key: `new-${Date.now()}`,
                            account_number: newAccountNumber.trim(),
                            description: newAccountDescription.trim(),
                            is_default: isFirst,
                            sort_order: prev.length,
                          },
                        ]);
                        setEditingAccountKey(null);
                        setNewAccountNumber('');
                        setNewAccountDescription('');
                      }}
                    />
                  </div>
                </div>
              ) : (
                <AddItemButton
                  gap='24px'
                  width='28px'
                  height='28px'
                  marginTop='0px'
                  text='Add Account Number'
                  fontSize='12px'
                  fontWeight='600'
                  onClick={() => {
                    setEditingAccountKey('__new__');
                    setNewAccountNumber('');
                    setNewAccountDescription('');
                  }}
                />
              )}
            </Col>
          </Row>
        </div>

        <div className='tp-details-footer'>
          <CustomButton
            text='Save'
            bgColor='#000'
            htmlType='submit'
            borderRadius='8px'
            color='#fff'
            fontSize='16px'
            fontWeight='600'
            height='48px'
            width='100%'
          />
          <CustomButton
            boxShadow={'none'}
            text='Cancel'
            bgColor='transparent'
            borderRadius='8px'
            color='var(--text-primary)'
            fontSize='16px'
            fontWeight='400'
            height='auto'
            width='100%'
            border='none'
            marginTop='15px'
            marginBottom='8px'
            onClick={onClose}
          />
        </div>
      </Form>
      <CustomDeleteModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={handleConfirmDelete}
        title='Remove Contact'
        message='Removing this contact will prevent selecting it for future use.'
        cancelText='Cancel'
        confirmText='Remove'
        confirmColor='var(--danger)'
      />
    </>
  );
};

export default TransportProviderDetails;
